// ═══════════════════════════════════════════════════════════════════════════════
// ARTIFACT - Recherche Artisans (LIVE DATA)
// Affiche les résultats de recherche d'artisans avec cards cliquables
// Coût: 0 crédit - Requête DB uniquement
// ═══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import {
  Star, MapPin, Phone, MessageCircle, Heart, ExternalLink,
  Filter, Check, Clock, Award, Zap, Search, Loader2, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════

export default function ArtifactSearchArtisans({ 
  initialQuery = '',
  initialFilters = {},
  onContact,
  onFavorite 
}) {
  const navigate = useNavigate();
  
  // États
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [showFilters, setShowFilters] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    disponible: false,
    urgence: false,
    certifie: false,
    minRating: 0,
    ...initialFilters
  });
  const [favorites, setFavorites] = useState([]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FETCH DATA
  // ═══════════════════════════════════════════════════════════════════════════
  
  const fetchArtisans = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams();
      if (searchQuery) params.append('query', searchQuery);
      if (activeFilters.disponible) params.append('disponible', 'true');
      if (activeFilters.urgence) params.append('urgence', 'true');
      if (activeFilters.certifie) params.append('certifie', 'true');
      if (activeFilters.minRating > 0) params.append('min_rating', activeFilters.minRating.toString());
      params.append('limit', '20');
      
      const response = await fetch(`${API_URL}/api/search/artisans?${params.toString()}`);
      
      if (!response.ok) {
        throw new Error('Erreur lors de la recherche');
      }
      
      const data = await response.json();
      setResults(data.results || []);
      setTotal(data.total || 0);
    } catch (err) {
      console.error('Search error:', err);
      setError('Impossible de charger les artisans. Réessaie.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, [searchQuery, activeFilters]);
  
  // Charger au montage et quand les filtres changent
  useEffect(() => {
    fetchArtisans();
  }, [fetchArtisans]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // HANDLERS
  // ═══════════════════════════════════════════════════════════════════════════
  
  const toggleFavorite = (id) => {
    setFavorites(prev => 
      prev.includes(id) ? prev.filter(f => f !== id) : [...prev, id]
    );
    if (onFavorite) onFavorite(id);
  };
  
  const handleContact = (artisan) => {
    if (onContact) {
      onContact(artisan);
    } else {
      navigate(`/messages?to=${artisan.id}`);
    }
  };
  
  const handleViewProfile = (artisan) => {
    navigate(`/boutique/${artisan.slug || artisan.id}`);
  };
  
  const handleSearch = (e) => {
    e.preventDefault();
    fetchArtisans();
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // RENDU
  // ═══════════════════════════════════════════════════════════════════════════
  
  return (
    <div className="h-full flex flex-col bg-gray-50">
      {/* Header avec recherche et filtres */}
      <div className="bg-white border-b border-gray-200 p-3">
        {/* Barre de recherche */}
        <form onSubmit={handleSearch} className="flex gap-2 mb-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Rechercher un artisan, métier, ville..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
            />
          </div>
          <button
            type="submit"
            disabled={loading}
            className="px-3 py-2 bg-[#1E3A5F] text-white rounded-lg hover:bg-[#2d5a8a] transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
          </button>
        </form>
        
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900">
              {loading ? 'Recherche...' : `${total} artisan${total > 1 ? 's' : ''} trouvé${total > 1 ? 's' : ''}`}
            </h3>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={fetchArtisans}
              disabled={loading}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
              title="Actualiser"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm transition-colors ${
                showFilters ? 'bg-[#1E3A5F] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <Filter className="w-4 h-4" />
              Filtres
              {Object.values(activeFilters).some(v => v) && (
                <span className="w-2 h-2 bg-orange-500 rounded-full" />
              )}
            </button>
          </div>
        </div>
        
        {/* Filtres rapides */}
        {showFilters && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            className="flex flex-wrap gap-2 pt-3 mt-3 border-t border-gray-100"
          >
            <FilterChip
              active={activeFilters.disponible}
              onClick={() => setActiveFilters(f => ({ ...f, disponible: !f.disponible }))}
              icon={Check}
              label="Disponible"
            />
            <FilterChip
              active={activeFilters.urgence}
              onClick={() => setActiveFilters(f => ({ ...f, urgence: !f.urgence }))}
              icon={Zap}
              label="Urgence"
            />
            <FilterChip
              active={activeFilters.certifie}
              onClick={() => setActiveFilters(f => ({ ...f, certifie: !f.certifie }))}
              icon={Award}
              label="Certifié"
            />
            <FilterChip
              active={activeFilters.minRating >= 4}
              onClick={() => setActiveFilters(f => ({ ...f, minRating: f.minRating >= 4 ? 0 : 4 }))}
              icon={Star}
              label="4+ étoiles"
            />
          </motion.div>
        )}
      </div>
      
      {/* Liste des résultats */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {error ? (
          <div className="text-center py-8">
            <p className="text-red-500 mb-2">{error}</p>
            <button
              onClick={fetchArtisans}
              className="text-sm text-[#1E3A5F] hover:underline"
            >
              Réessayer
            </button>
          </div>
        ) : loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map(i => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                <div className="flex gap-3">
                  <div className="w-14 h-14 bg-gray-200 rounded-xl" />
                  <div className="flex-1">
                    <div className="h-4 bg-gray-200 rounded w-1/2 mb-2" />
                    <div className="h-3 bg-gray-200 rounded w-1/3" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : results.length === 0 ? (
          <div className="text-center py-8">
            <p className="text-gray-500">Aucun artisan ne correspond à ta recherche</p>
            <button
              onClick={() => {
                setSearchQuery('');
                setActiveFilters({ disponible: false, urgence: false, certifie: false, minRating: 0 });
              }}
              className="mt-2 text-sm text-[#1E3A5F] hover:underline"
            >
              Réinitialiser les filtres
            </button>
          </div>
        ) : (
          results.map((artisan, idx) => (
            <motion.div
              key={artisan.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: idx * 0.05 }}
              className="bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-shadow"
            >
              {/* Card header */}
              <div className="p-3">
                <div className="flex gap-3">
                  {/* Avatar */}
                  <div className="relative flex-shrink-0">
                    {artisan.avatar ? (
                      <img
                        src={artisan.avatar}
                        alt={artisan.name}
                        className="w-14 h-14 rounded-xl object-cover"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#1E3A5F] to-[#3B82F6] flex items-center justify-center text-white font-bold text-lg">
                        {artisan.name?.[0]?.toUpperCase() || 'A'}
                      </div>
                    )}
                    {artisan.disponible && (
                      <span className="absolute -bottom-1 -right-1 w-4 h-4 bg-emerald-500 border-2 border-white rounded-full" />
                    )}
                  </div>
                  
                  {/* Infos */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold text-gray-900 truncate">{artisan.name}</h4>
                        <p className="text-sm text-gray-500">{artisan.metier}</p>
                      </div>
                      <button
                        onClick={() => toggleFavorite(artisan.id)}
                        className={`p-1.5 rounded-lg transition-colors ${
                          favorites.includes(artisan.id)
                            ? 'text-red-500 bg-red-50'
                            : 'text-gray-400 hover:bg-gray-100'
                        }`}
                      >
                        <Heart className={`w-4 h-4 ${favorites.includes(artisan.id) ? 'fill-current' : ''}`} />
                      </button>
                    </div>
                    
                    {/* Rating & Location */}
                    <div className="flex items-center gap-3 mt-1 text-xs">
                      <span className="flex items-center gap-1 text-amber-600">
                        <Star className="w-3.5 h-3.5 fill-current" />
                        {artisan.rating?.toFixed(1) || '0.0'} ({artisan.reviews_count || 0})
                      </span>
                      {artisan.location?.city && (
                        <span className="flex items-center gap-1 text-gray-500">
                          <MapPin className="w-3.5 h-3.5" />
                          {artisan.location.city}
                          {artisan.location.distance_km && ` • ${artisan.location.distance_km} km`}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Description */}
                {artisan.description && (
                  <p className="text-xs text-gray-600 mt-2 line-clamp-2">{artisan.description}</p>
                )}
                
                {/* Tags */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {artisan.urgence_disponible && (
                    <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs flex items-center gap-1">
                      <Zap className="w-3 h-3" /> Urgence
                    </span>
                  )}
                  {artisan.certifications?.slice(0, 2).map(cert => (
                    <span key={cert} className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-full text-xs">
                      {cert}
                    </span>
                  ))}
                  {artisan.response_time && (
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-xs flex items-center gap-1">
                      <Clock className="w-3 h-3" /> {artisan.response_time}
                    </span>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex border-t border-gray-100">
                <button
                  onClick={() => handleContact(artisan)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 text-[#1E3A5F] hover:bg-[#1E3A5F]/5 transition-colors text-sm font-medium"
                >
                  <MessageCircle className="w-4 h-4" />
                  Contacter
                </button>
                <div className="w-px bg-gray-100" />
                <button
                  onClick={() => handleViewProfile(artisan)}
                  className="flex-1 flex items-center justify-center gap-2 py-2.5 text-gray-600 hover:bg-gray-50 transition-colors text-sm"
                >
                  <ExternalLink className="w-4 h-4" />
                  Voir le profil
                </button>
              </div>
            </motion.div>
          ))
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT FILTER CHIP
// ═══════════════════════════════════════════════════════════════════════════════

function FilterChip({ active, onClick, icon: Icon, label }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
        active
          ? 'bg-[#1E3A5F] text-white'
          : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
      }`}
    >
      <Icon className="w-3 h-3" />
      {label}
    </button>
  );
}
